import type { MedioPagoPos, PagoTicket } from './tipos'

interface Situacion {
  /** Los pagos ya cargados a mano. Con uno solo que haya, no hay implícito. */
  pagos: PagoTicket[]

  /** El medio que está seleccionado en la pantalla. */
  medio: MedioPagoPos | null

  /** Cliente de cuenta corriente, cuando el medio es fiado. */
  cliente: { id: number } | null

  /** Lo tipeado en el campo de la referencia (cupón, lote, comprobante). */
  referencia: string

  /** El importe que muestra el teclado: lo tipeado, o el faltante. */
  importeCentavos: number

  /** El total del ticket, ya con el redondeo de efectivo si corresponde. */
  totalCentavos: number
}

/**
 * El pago que se da por entendido: un solo medio, por el total.
 *
 * Es el caso mayoritario del mostrador y hasta acá pedía un paso de más. El
 * cajero elegía Efectivo, tipeaba con cuánto paga, veía el vuelto… y el panel
 * le decía que faltaba el total y el botón que cobra estaba apagado, porque
 * antes había que apretar «Agregar el pago». Un toque más en la operación que
 * se repite trescientas veces por día, y encima uno que se lee como un sistema
 * que no anda: todo está completo y no deja seguir.
 *
 * Es la misma regla que ya tenía la caja del solo-cobro (ver
 * `cobroSinDetalle.ts`): dos pantallas que cobran no pueden tener dos
 * criterios. Y vive acá y no adentro del diálogo porque decide el IMPORTE de
 * un pago, y ese número lo compara el servidor contra el total.
 *
 * Devuelve null —o sea, hay que agregar los pagos a mano— en cuatro casos:
 *
 * - **Ya hay pagos cargados.** Apenas se divide el cobro manda lo cargado;
 *   sumarle además un implícito cobraría de más.
 * - **Lo tipeado es MENOR que el total.** Tipear menos es justamente cómo se
 *   empieza a dividir, y ahí la pantalla no puede decidir por el cajero. Con
 *   el campo intacto el importe ES el faltante, así que el caso normal entra
 *   solo.
 * - **El medio pide referencia y no está.** Es el número de cupón con el que
 *   después se concilia contra el resumen.
 * - **Es fiado y no hay cliente.** Sin cliente la deuda no tiene dueño.
 *
 * El importe implícito es SIEMPRE el total exacto y nunca lo tipeado: con
 * cuánto paga el cliente se dice en «¿Con cuánto paga?», que es lo que calcula
 * el vuelto, y cobrar de más porque alguien tipeó un billete en el campo
 * equivocado es plata que el arqueo después no puede explicar.
 */
export function pagoImplicito({
  pagos,
  medio,
  cliente,
  referencia,
  importeCentavos,
  totalCentavos,
}: Situacion): { medio: MedioPagoPos; importeCentavos: number; referencia?: string } | null {
  if (pagos.length > 0) return null
  if (medio === null) return null
  if (totalCentavos <= 0) return null
  if (importeCentavos < totalCentavos) return null

  const limpia = referencia.trim()

  if (medio.pideReferencia && limpia.length === 0) return null
  if (medio.esCuentaCorriente && cliente === null) return null

  return {
    medio,
    importeCentavos: totalCentavos,
    referencia: medio.pideReferencia ? limpia : undefined,
  }
}
